import { SettingsList } from './SettingsList';
import { API_MESSAGE_INVALID_SESSION } from '@standardnotes/services';
import { getErrorFromErrorResponse, HttpStatusCode, isErrorResponse } from '@standardnotes/responses';
/**
 * SettingsGateway coordinates communication with the API service
 * wrapping the userUuid provision for simpler consumption
 */
export class SettingsGateway {
    constructor(settingsApi, userProvider) {
        this.settingsApi = settingsApi;
        this.userProvider = userProvider;
    }
    isReadyForModification() {
        return this.getUser() != undefined;
    }
    getUser() {
        return this.userProvider.getUser();
    }
    get userUuid() {
        const user = this.getUser();
        if (user == undefined || user.uuid == undefined) {
            throw new Error(API_MESSAGE_INVALID_SESSION);
        }
        return user.uuid;
    }
    async listSettings() {
        const response = await this.settingsApi.listSettings(this.userUuid);
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
        if (response.data == undefined || response.data.settings == undefined) {
            return new SettingsList([]);
        }
        const settings = new SettingsList(response.data.settings);
        return settings;
    }
    async getSetting(name, serverPassword) {
        var _a, _b, _c;
        const response = await this.settingsApi.getSetting(this.userUuid, name.value, serverPassword);
        if (response.status === HttpStatusCode.BadRequest) {
            return undefined;
        }
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
        return (_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.setting) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : undefined;
    }
    async getSubscriptionSetting(name) {
        var _a, _b, _c;
        if (!name.isASubscriptionSetting()) {
            throw new Error(`Setting ${name.value} is not a subscription setting`);
        }
        const response = await this.settingsApi.getSubscriptionSetting(this.userUuid, name.value);
        if (response.status === HttpStatusCode.BadRequest) {
            return undefined;
        }
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
        return (_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.setting) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : undefined;
    }
    async updateSubscriptionSetting(name, payload, sensitive) {
        const response = await this.settingsApi.updateSubscriptionSetting(this.userUuid, name.value, payload, sensitive);
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
    }
    async getDoesSensitiveSettingExist(name) {
        var _a, _b;
        if (!name.isSensitive()) {
            throw new Error(`Setting ${name.value} is not sensitive`);
        }
        const response = await this.settingsApi.getSetting(this.userUuid, name.value);
        if (response.status === HttpStatusCode.BadRequest) {
            return false;
        }
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
        return (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.success) !== null && _b !== void 0 ? _b : false;
    }
    async updateSetting(name, payload, sensitive, totpToken) {
        const response = await this.settingsApi.updateSetting(this.userUuid, name.value, payload, sensitive, totpToken);
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
    }
    async deleteSetting(name, serverPassword) {
        const response = await this.settingsApi.deleteSetting(this.userUuid, name.value, serverPassword);
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
    }
    async getMfaSecret() {
        const response = await this.settingsApi.getMfaSecret(this.userUuid);
        if (isErrorResponse(response)) {
            throw new Error(getErrorFromErrorResponse(response).message);
        }
        return response.data.secret;
    }
    deinit() {
        ;
        this.settingsApi = undefined;
        this.userProvider = undefined;
    }
}
